import { useStackNavigate } from '@/app/navigation/stack-links';
import { Viewer } from '@/components/editor/Viewer';
import {
  Panel,
  type PanelAction,
  type RoutedPanelProps,
} from '@/components/Layout/Panel/Panel';
import {
  Timeline,
  TimelineEntry,
  TimelineAttachmentContent,
  TimelineLocationContent,
  MessageComposer,
  TimelineEventDescription,
  isUserGroupStart,
  getTimelineIcon,
} from '@/components/Timeline';
import {
  canViewInLightbox,
  Lightbox,
  LightboxRef,
} from '@/components/ui/Lightbox';
import { Button } from '@/components/ui/button';
import { useUserMap } from '@/features/users/users.hooks';
import {
  findNode,
  findNodePath,
  getLeafFields,
  type AddAttachmentEvent,
  type AddAttachmentPayload,
  type AddGeotagEvent,
  type EditGeotagEvent,
  type Field,
  type ListEvent,
  type NumericField,
  type Position,
  type User,
  type ValueField,
} from '@certhon/models';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Trash2,
} from 'lucide-react';
import { useCallback, useMemo, useRef } from 'react';
import {
  ListProvider,
  useListContext,
  useFieldActions,
} from '@/features/lists/context';

// TODO: The Editor/MessageComposer depends on TicketsStoreProvider for #ticket
// references. This works but couples checklists to the ticket plugin. When we
// properly support cross-entity references in checklist comments, extract the
// editor's entity-reference system into a generic provider.
import { useCreateTicketStore } from '@/components/editor/TicketPlugin/TicketPlugin';
import { TicketsStoreProvider } from '@/components/editor/TicketPlugin/TicketsStoreProvider';
import { uploadAttachment } from '@/features/attachments/uploadAttachment';
import { CheckpointControl } from '@/features/lists/components/CheckpointControl';
import { FeedbackReviewSection } from '@/features/feedback/components/FeedbackReviewSection';
import { FeedbackSection } from '@/features/feedback/components/FeedbackSection';
import { FieldOriginSection } from '@/features/lists/components/FieldOriginSection';
import { FieldPropertiesForm } from '@/features/lists/components/FieldPropertiesForm';
import { NumericControl } from '@/features/lists/components/NumericControl';
import { resolveLocalized } from '@/features/lists/components/PreviewLocaleContext';
import { TaskControl } from '@/features/lists/components/TaskControl';

export const QuestionDetailPanel = ({
  params,
  basePath,
  ...panelProps
}: RoutedPanelProps) => {
  const checklistId = params.checklistId || params.templateId;
  const questionId = params.questionId || params.nodeId;
  const ticketsStore = useCreateTicketStore();

  if (!questionId) {
    return (
      <Panel {...panelProps} title="Question">
        <p className="text-muted-foreground text-sm">No question selected.</p>
      </Panel>
    );
  }

  return (
    <ListProvider listId={checklistId}>
      <TicketsStoreProvider store={ticketsStore}>
        <QuestionDetailPanelContent
          checklistId={checklistId}
          questionId={questionId}
          basePath={basePath}
          {...panelProps}
        />
      </TicketsStoreProvider>
    </ListProvider>
  );
};

// ---------------------------------------------------------------------------

interface ContentProps extends Omit<RoutedPanelProps, 'params'> {
  checklistId: string;
  questionId: string;
}

function QuestionDetailPanelContent({
  checklistId: _checklistId,
  questionId,
  basePath,
  ...panelProps
}: ContentProps) {
  const { builtList, list, mode } = useListContext();
  const navigate = useStackNavigate();
  const userMap = useUserMap('recnum');

  const field = useMemo(
    () => findNode(builtList.tree, questionId),
    [builtList.tree, questionId],
  );

  // Breadcrumb path from root to this node (for section context)
  const nodePath = useMemo(
    () => findNodePath(builtList.tree, questionId),
    [builtList.tree, questionId],
  );

  // Prev/next leaf-level navigation
  const leafFields = useMemo(
    () => getLeafFields(builtList.tree),
    [builtList.tree],
  );

  const currentLeafIdx = useMemo(
    () => leafFields.findIndex((f) => f.id === questionId),
    [leafFields, questionId],
  );

  const prevField = currentLeafIdx > 0 ? leafFields[currentLeafIdx - 1] : null;
  const nextField =
    currentLeafIdx < leafFields.length - 1
      ? leafFields[currentLeafIdx + 1]
      : null;

  // Navigate to a different question (prev/next) while staying in details panel.
  // basePath is .../checklistId/questionId/details — strip 2 segments to get
  // back to the checklist level, then append new questionId/details.
  const checklistBase = useMemo(
    () => basePath.replace(/\/[^/]+\/[^/]+$/, ''),
    [basePath],
  );

  const navigateToQuestion = useCallback(
    (fieldId: string) => {
      navigate({ to: `${checklistBase}/${fieldId}/details` });
    },
    [navigate, checklistBase],
  );

  // Navigate back to the list, preserving questionId so the tree highlights
  // (and auto-scrolls to) the question the user just left. Strips the trailing
  // `/details` only.
  const navigateBack = useCallback(() => {
    navigate({ to: basePath.replace(/\/details$/, '') });
  }, [navigate, basePath]);

  // Filter events relevant to this field. Beyond `targetNodeId` (events
  // *affecting* a parent that owns this field), include events whose subject
  // IS this field — its creation (INSERT carrying `node.id`), structural
  // moves/copies (`subjectNodeId`), and the event that originally produced
  // this node (`field.originEventId` — set by INSERT/COPY/EXPAND_PROTO).
  const originEventId =
    field && 'originEventId' in field ? field.originEventId : undefined;
  const fieldEvents = useMemo(() => {
    const targetEvents = list.events.filter((e) => {
      if (e.id === originEventId) return true;
      if (!('payload' in e) || !e.payload) return false;
      const payload = e.payload as Record<string, unknown>;
      if (payload.targetNodeId === questionId) return true;
      // Subject events only matter when the node was moved — being the
      // *source* of a copy is irrelevant for this field's history.
      if (payload.subjectNodeId === questionId && e.type === 'MOVE') {
        return true;
      }
      const node = payload.node as { id?: string } | undefined;
      if (node && node.id === questionId) return true;
      return false;
    });
    // Sort newest first
    return [...targetEvents].sort(
      (a, b) => b.created.getTime() - a.created.getTime(),
    );
  }, [list.events, questionId, originEventId]);

  // Edit-mode-only delete action. Hooks must run before the !field early
  // return; the action is only invoked while the field exists, and the
  // computed action list is gated by mode below.
  const fieldActions = useFieldActions(questionId);
  const isGroup = field?.type === 'GROUP_FIELD';
  const groupHasChildren = isGroup && field.children.length > 0;
  const handleDelete = useCallback(() => {
    if (groupHasChildren) {
      const confirmed = window.confirm(
        'This group contains fields. Remove it and all its children?',
      );
      if (!confirmed) return;
    }
    fieldActions.remove();
    navigateBack();
  }, [fieldActions, navigateBack, groupHasChildren]);

  if (!field) {
    return (
      <Panel {...panelProps} title="Question">
        <p className="text-muted-foreground text-sm">
          Question not found in checklist.
        </p>
      </Panel>
    );
  }

  // Build breadcrumb from path (skip ROOT_FIELD, show GROUP_FIELD titles,
  // exclude the target node itself since it's shown as the panel title)
  const breadcrumbParts = nodePath
    ? nodePath
        .slice(0, -1) // exclude current node
        .filter(
          (n): n is Field & { title_nl: string; title_en: string } =>
            n.type === 'GROUP_FIELD',
        )
        .map((n) => resolveLocalized(n.title_nl, n.title_en, 'nl'))
    : [];

  const defaultTitle = field.type === 'GROUP_FIELD' ? 'Group' : 'Question';
  const fieldTitle =
    'title_nl' in field
      ? resolveLocalized(
          (field as ValueField).title_nl,
          (field as ValueField).title_en,
          'nl',
        ) || defaultTitle
      : defaultTitle;

  const headerActions = (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        disabled={!prevField}
        onClick={() => prevField && navigateToQuestion(prevField.id)}
        aria-label="Previous question"
      >
        <ChevronUp className="h-4 w-4" />
      </Button>
      <span className="text-muted-foreground text-xs tabular-nums">
        {currentLeafIdx + 1}/{leafFields.length}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        disabled={!nextField}
        onClick={() => nextField && navigateToQuestion(nextField.id)}
        aria-label="Next question"
      >
        <ChevronDown className="h-4 w-4" />
      </Button>
    </div>
  );

  // Edit-mode-only overflow menu (delete, future structural actions).
  // Empty array in non-edit modes hides the kebab entirely (Panel checks length).
  const panelActions: PanelAction[] =
    mode === 'edit'
      ? [
          {
            icon: Trash2,
            label: `Delete ${isGroup ? 'group' : 'field'}`,
            onCalled: handleDelete,
            variant: 'destructive',
          },
        ]
      : [];

  return (
    <Panel
      {...panelProps}
      title={fieldTitle}
      headerActions={headerActions}
      actions={panelActions}
    >
      {/* Back button */}
      <button
        type="button"
        onClick={navigateBack}
        className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1 text-sm
          transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to list
      </button>

      {/* Section breadcrumb */}
      {breadcrumbParts.length > 0 && (
        <div className="text-muted-foreground mb-2 text-xs">
          {breadcrumbParts.join(' > ')} &gt; {fieldTitle}
        </div>
      )}

      {mode === 'edit' ? (
        <EditModeContent field={field} />
      ) : (
        <FillInModeContent
          field={field}
          fieldEvents={fieldEvents}
          userMap={userMap}
          questionId={questionId}
          mode={mode}
          listId={list.id}
          onSelectField={navigateToQuestion}
        />
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Edit mode: field properties form
// ---------------------------------------------------------------------------

function EditModeContent({ field }: { field: Field }) {
  const { list, isTemplate } = useListContext();
  const actions = useFieldActions(field.id);

  return (
    <>
      <FieldPropertiesForm field={field} onPatch={actions.patch} />
      <FieldOriginSection field={field} />
      {isTemplate && (
        <FeedbackReviewSection protoListId={list.id} protoNodeId={field.id} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Fill-in / view mode: answer control + timeline
// ---------------------------------------------------------------------------

function FillInModeContent({
  field,
  fieldEvents,
  userMap,
  questionId,
  mode,
  listId,
  onSelectField,
}: {
  field: Field;
  fieldEvents: ListEvent[];
  userMap: Map<number, User>;
  questionId: string;
  mode: string;
  listId: string;
  onSelectField?: (fieldId: string) => void;
}) {
  const { user, isTemplate, list } = useListContext();
  // For GROUP_FIELD in view/fill-in mode, show children as a clickable list
  if (field.type === 'GROUP_FIELD') {
    return (
      <div className="space-y-1">
        {field.children.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            This group has no fields.
          </p>
        ) : (
          field.children.map((child) => {
            const childTitle =
              'title_nl' in child
                ? (child as ValueField).title_nl ||
                  (child as ValueField).title_en ||
                  '(untitled)'
                : '(untitled)';
            return (
              <button
                key={child.id}
                type="button"
                onClick={() => onSelectField?.(child.id)}
                className="hover:bg-muted/40 flex w-full items-center gap-2 rounded px-2 py-2 text-left
                  text-sm transition-colors"
              >
                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">{childTitle}</span>
              </button>
            );
          })
        )}
      </div>
    );
  }

  return (
    <>
      {/* Description */}
      {field.type !== 'ROOT_FIELD' && (
        <>
          {((field as ValueField).description_nl ||
            (field as ValueField).description_en) && (
            <p className="text-muted-foreground mb-4 text-sm">
              {(field as ValueField).description_nl ||
                (field as ValueField).description_en}
            </p>
          )}
        </>
      )}

      {/* Large answer control */}
      <QuestionAnswer field={field} />

      {/* Timeline section */}
      <QuestionTimeline
        field={field}
        fieldEvents={fieldEvents}
        userMap={userMap}
        questionId={questionId}
        originEventId={
          'originEventId' in field ? field.originEventId : undefined
        }
        disabled={mode === 'view'}
      />

      {/* Internal feedback section (technician submit) */}
      <FeedbackSection
        field={field}
        listId={listId}
        userId={user.recnum}
        disabled={mode === 'view'}
      />

      {/* Feedback review section (template admin view) */}
      {isTemplate && (
        <FeedbackReviewSection protoListId={list.id} protoNodeId={field.id} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Large answer controls
// ---------------------------------------------------------------------------

function QuestionAnswer({ field }: { field: Field }) {
  const { mode } = useListContext();
  const actions = useFieldActions(field.id);
  const disabled = mode === 'view';

  if (field.type === 'ROOT_FIELD' || field.type === 'GROUP_FIELD') {
    return null;
  }

  return (
    <div className="mb-6">
      <label className="text-muted-foreground mb-2 block text-xs font-medium tracking-wide uppercase">
        Answer
      </label>
      {field.type === 'CHECKPOINT_FIELD' && (
        <CheckpointControl
          size="lg"
          value={field.value}
          onChange={(v) => actions.patch({ value: v })}
          disabled={disabled}
        />
      )}
      {field.type === 'NUMERIC_FIELD' && (
        <NumericControl
          size="lg"
          value={field.value}
          onChange={(v) => actions.patch({ value: v })}
          disabled={disabled}
          unit={(field as NumericField).unit}
          placeholder="Enter value..."
        />
      )}
      {field.type === 'TASK_FIELD' && (
        <TaskControl
          size="lg"
          value={field.value}
          onChange={(v) => actions.patch({ value: v })}
          disabled={disabled}
          title={field.title_nl || field.title_en || 'Task'}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline section
// ---------------------------------------------------------------------------

function getFieldEventIcon(event: ListEvent) {
  return getTimelineIcon(event.type);
}

function getFieldEventDescription(
  event: ListEvent,
  questionId: string,
  originEventId: string | undefined,
): string {
  switch (event.type) {
    case 'PATCH': {
      const keys = Object.keys(event.payload.partialNode);
      if (keys.includes('value')) return 'recorded an answer';
      return `updated ${keys.join(', ')}`;
    }
    case 'ADD_NOTE':
      return 'posted a message';
    case 'EDIT_NOTE':
      return 'edited a message';
    case 'REMOVE_NOTE':
      return 'removed a message';
    case 'ADD_ATTACHMENT': {
      const filename =
        (event as AddAttachmentEvent).payload.attachment.filename ||
        'attachment';
      return `uploaded ${filename}`;
    }
    case 'REMOVE_ATTACHEMNT':
      return 'removed a file';
    case 'ADD_GEOTAG': {
      const label = (event as AddGeotagEvent).payload.geotag.label;
      return label ? `added location "${label}"` : 'added location';
    }
    case 'EDIT_GEOTAG': {
      const label = (event as EditGeotagEvent).payload.geotag.label;
      return label ? `updated location "${label}"` : 'updated location';
    }
    case 'REMOVE_GEOTAG':
      return 'removed a location';
    case 'INSERT':
      return event.payload.node.id === questionId
        ? 'created this field'
        : 'added a field here';
    case 'REMOVE':
      return 'removed this field';
    case 'MOVE':
      return event.payload.subjectNodeId === questionId
        ? 'moved this field'
        : 'moved a field here';
    case 'COPY':
      if (event.id === originEventId) return 'duplicated this field';
      return event.payload.subjectNodeId === questionId
        ? 'copied this field'
        : 'copied a field here';
    case 'EXPAND_PROTO':
      return event.id === originEventId
        ? 'inserted using a template'
        : 'expanded a template here';
    case 'CREATE_LIST':
      return 'created the list';
    case 'MODIFY_LIST':
      return 'updated list metadata';
    case 'CLOSE_LIST':
      return 'closed the list';
    case 'OPEN_LIST':
      return 'reopened the list';
    default:
      return 'performed an action';
  }
}

/** Returns the note/message text for events that have rich markdown content. */
function getFieldEventNoteContent(event: ListEvent): string | null {
  switch (event.type) {
    case 'ADD_NOTE':
      return event.payload.note;
    case 'EDIT_NOTE':
      return event.payload.text;
    default:
      return null;
  }
}

/** Returns a plain-text detail string for non-note events (e.g. PATCH answers). */
function getFieldEventDetail(event: ListEvent): string | null {
  switch (event.type) {
    case 'PATCH': {
      const val = event.payload.partialNode.value;
      if (val === true) return 'Pass';
      if (val === false) return 'Fail';
      if (typeof val === 'number') return String(val);
      if (val === null) return 'Cleared';
      return null;
    }
    default:
      return null;
  }
}

function QuestionTimeline({
  field,
  fieldEvents,
  userMap,
  questionId,
  originEventId,
  disabled,
}: {
  field: Field;
  fieldEvents: ListEvent[];
  userMap: Map<number, User>;
  questionId: string;
  originEventId: string | undefined;
  disabled: boolean;
}) {
  const actions = useFieldActions(questionId);
  const { isTemplate } = useListContext();
  const lightboxRef = useRef<LightboxRef>(null);

  // Collect all viewable attachments from events for Lightbox navigation
  const lightboxItems: AddAttachmentPayload[] = useMemo(
    () =>
      fieldEvents
        .filter(
          (e): e is AddAttachmentEvent =>
            e.type === 'ADD_ATTACHMENT' &&
            canViewInLightbox(e.payload.attachment.contentType),
        )
        .map((e) => ({
          checksum: e.payload.attachment.checksum,
          contentType: e.payload.attachment.contentType,
          size: e.payload.attachment.size,
          filename: e.payload.attachment.filename ?? undefined,
        })),
    [fieldEvents],
  );

  const handleOpenInLightbox = useCallback(
    (attachment: AddAttachmentPayload) => {
      lightboxRef.current?.open(attachment.checksum);
    },
    [],
  );

  const handleSendMessage = useCallback(
    async (message: string) => {
      actions.addNote(message);
    },
    [actions],
  );

  const handleUploadAttachments = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const attachment = await uploadAttachment(file);
        actions.addAttachment(attachment);
      }
    },
    [actions],
  );

  const handleAddLocation = useCallback(
    (location: { position: Position; label: string }) => {
      actions.addGeotag({
        label: location.label,
        position: location.position,
      });
    },
    [actions],
  );

  if (field.type === 'ROOT_FIELD' || field.type === 'GROUP_FIELD') {
    return null;
  }

  return (
    <div className="mt-8">
      <h2 className="text-foreground text-sm font-semibold">Timeline</h2>

      {/* Timeline entries. Composer hidden on templates (history is read-only) */}
      <Timeline
        composer={
          !isTemplate ? (
            <MessageComposer
              onSendMessage={handleSendMessage}
              onUploadAttachments={handleUploadAttachments}
              onAddLocation={handleAddLocation}
              disabled={disabled}
              placeholder="Add a message or upload files..."
            />
          ) : undefined
        }
      >
        {fieldEvents.map((event, index) => {
          const prevEvent = index > 0 ? fieldEvents[index - 1] : null;
          const isGroupStart = isUserGroupStart(
            event.user_id,
            prevEvent?.user_id,
          );
          const isLastEvent = index === fieldEvents.length - 1;

          const user = userMap.get(event.user_id) ?? {
            recnum: event.user_id,
            name: `User ${event.user_id}`,
            loginname: '',
          };

          const Icon = getFieldEventIcon(event);
          const description = getFieldEventDescription(
            event,
            questionId,
            originEventId,
          );
          const noteContent = getFieldEventNoteContent(event);
          const detail = getFieldEventDetail(event);

          // Items are newest-first; the chronologically previous (older)
          // event sits at index + 1.
          const prevTimestamp =
            index < fieldEvents.length - 1
              ? fieldEvents[index + 1].created
              : undefined;

          return (
            <TimelineEntry
              key={event.id}
              timestamp={event.created}
              previousTimestamp={prevTimestamp}
              user={user}
              isGroupStart={isGroupStart}
              isLastEvent={isLastEvent}
              icon={<Icon className="text-muted-foreground/80 h-4 w-4" />}
              description={
                <TimelineEventDescription
                  userName={user.name ?? `User ${event.user_id}`}
                >
                  {description}
                </TimelineEventDescription>
              }
            >
              {noteContent && <Viewer value={noteContent} />}

              {detail && (
                <p className="text-muted-foreground mt-0.5 text-xs italic">
                  {detail}
                </p>
              )}

              {event.type === 'ADD_ATTACHMENT' && (
                <TimelineAttachmentContent
                  checksum={
                    (event as AddAttachmentEvent).payload.attachment.checksum
                  }
                  contentType={
                    (event as AddAttachmentEvent).payload.attachment.contentType
                  }
                  size={(event as AddAttachmentEvent).payload.attachment.size}
                  filename={
                    (event as AddAttachmentEvent).payload.attachment.filename
                  }
                  onOpenInLightbox={handleOpenInLightbox}
                />
              )}

              {event.type === 'ADD_GEOTAG' && (
                <TimelineLocationContent
                  position={(event as AddGeotagEvent).payload.geotag.position}
                  label={
                    (event as AddGeotagEvent).payload.geotag.label ?? 'Location'
                  }
                />
              )}

              {event.type === 'EDIT_GEOTAG' &&
                (event as EditGeotagEvent).payload.geotag.position && (
                  <TimelineLocationContent
                    position={
                      (event as EditGeotagEvent).payload.geotag
                        .position as Position
                    }
                    label={
                      (event as EditGeotagEvent).payload.geotag.label ??
                      'Location'
                    }
                  />
                )}
            </TimelineEntry>
          );
        })}
      </Timeline>

      <Lightbox ref={lightboxRef} attachments={lightboxItems} />
    </div>
  );
}
